import React from "react";
import { Alert, Button } from "@mui/material";
import { EntryValidation } from "../utils/transcriptValidation";

interface EntryStatusBannerProps {
  validation: EntryValidation;
  /** Arm this entry's range in the video timeline. */
  onSetRange: () => void;
}

/**
 * Inline validation banner for an entry: prompts to re-confirm the time range
 * after a large text edit, flags an invalid range, or an empty entry. Renders
 * nothing when the entry is valid.
 */
export const EntryStatusBanner: React.FC<EntryStatusBannerProps> = ({
  validation,
  onSetRange,
}) => {
  const { needsRangeReselect, invalidRange, emptyText } = validation;

  if (emptyText) {
    return (
      <Alert severity="error" sx={{ mb: 1 }}>
        This entry has no text. Add content, or delete the entry.
      </Alert>
    );
  }

  if (invalidRange) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 1 }}
        action={
          <Button color="inherit" size="small" onClick={onSetRange}>
            Set range
          </Button>
        }
      >
        This entry&apos;s start/end time is invalid.
      </Alert>
    );
  }

  if (needsRangeReselect) {
    return (
      <Alert
        severity="warning"
        sx={{ mb: 1 }}
        action={
          <Button color="inherit" size="small" onClick={onSetRange}>
            Set range
          </Button>
        }
      >
        You changed the wording substantially — re-confirm this entry&apos;s
        start/end on the video before saving.
      </Alert>
    );
  }

  return null;
};
